import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomUrlSchemeHandler } from '../../handler/CustomUrlSchemeHandler';
import { Attachment, PIBEmailDetails, PIBEmailList } from '@pos-app/data/lib/models/email';
import { SECURITY_FUNCTION } from '@pos-app/data';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { UserEmailViewGridActionRendererComponent } from './UserEmailViewGridActionRendererComponent';
import { ApiService, DialogService } from '@pos-app/core-ui';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-lib-user-email-view',
    templateUrl: './user-email-view.component.html',
    styleUrls: ['./user-email-view.component.scss'],
    standalone: false
})
export class UserEmailViewComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('emailDetailContent') emailDetailContent: ElementRef;
  @ViewChild('emailListContent') emailListContent: ElementRef;
  @Input() title: string;
  @Input() userEmailList: PIBEmailList[];
  @Input() loading: boolean;
  @Input() userSeletedEmailDetails: PIBEmailDetails;
  @Input() securityFunctions: string[];
  @Input() isInternalUser: boolean;
  @Input() showEmptyMessage: string;
  isShowRows: boolean;
  @Output() selectedEmail = new EventEmitter<PIBEmailDetails>();
  @Output() updatedUserEmailList = new EventEmitter<boolean>();
  isShowUserEmailDetails: boolean = false;
  page: number = 1;
  pageSize: number = 6;
  collectionSize: number = 0;
  pageViewChanged: boolean = false;
  paginateData: any[] = [];
  safeHtmlBody: SafeHtml;
  filteredAttachments: Attachment[];
  userSeletedEmailDetailsData: PIBEmailDetails;
  SECURITY_FUNCTION = SECURITY_FUNCTION;
  isAdminUser: boolean = false;
  //ag-grid
  gridOptions: GridOptions;
  columnDefs: ColDef[];
  searchValue: string;
  frameworkComponents;
  context;
  rowData = [];
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  private unSubscribe$ = new Subject<void>();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();
  isAddingNewEmailBulletin: boolean = false;
  isEditingEmailBulletin: boolean = false;

  constructor(
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private customUrlSchemeHandler: CustomUrlSchemeHandler,
    private dialogService: DialogService,
    private toastr: ToastrService,
    private apiService: ApiService
  ) {
    this.frameworkComponents = {
      actionRenderer: UserEmailViewGridActionRendererComponent,
    };
    this.context = { componentParent: this };
    this.gridOptions = this.gridOptionsData();
  }

  ngOnInit(): void {
    this.initializeData();
  }
  initializeData() {
    this.rowData = this.userEmailList;
    this.getPaginationData();
    this.collectionSize = this.userEmailList.length;
    this.isAdminUser = this.securityFunctions && this.securityFunctions.indexOf(SECURITY_FUNCTION.adminUser) !== -1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userSeletedEmailDetails'] && changes['userSeletedEmailDetails'].currentValue) {
      this.userSeletedEmailDetails = changes['userSeletedEmailDetails'].currentValue;
      this.showProductInfoBulletinEmailDetail(this.userSeletedEmailDetails);
    }
    if (changes['loading']) {
      this.loadingSubject.next(!!changes['loading'].currentValue);
    }
    if (changes['userEmailList'] && changes['userEmailList'].currentValue) {
      this.initializeData();
    }
  }

  getProductSummary(pibEmail) {
    return `PIB ${pibEmail.subject.split('-')[0]?.split('No.')[1]?.trim()} - ${this.getInShortDesc(
      pibEmail.subject.split('-')[1]
    )} (${this.getFormatedDate(pibEmail.dateSent)})`;
  }

  getFormatedDate(date) {
    return date.split('/')[0] + '/' + date.split('/')[1];
  }

  getInShortDesc(desc: string) {
    return desc.length > 45 ? desc.substring(0, 45) + '...' : desc;
  }

  getPaginationData() {
    this.paginateData = this.userEmailList?.slice((this.page - 1) * this.pageSize, (this.page - 1) * this.pageSize + this.pageSize);
  }

  selectPage(page: string) {
    this.page = parseInt(page, 10) || 1;
  }

  showSelectedEmailDetails(pibEmail: PIBEmailDetails) {
    this.selectedEmail.emit(pibEmail);
  }

  showProductInfoBulletinEmailDetail(data) {
    this.userSeletedEmailDetailsData = data;
    this.isShowUserEmailDetails = true;
    this.filteredAttachments = this.userSeletedEmailDetailsData.attachments.filter(
      (attachment) => !this.userSeletedEmailDetailsData.body.includes(attachment.filename)
    );
    this.safeHtmlBody = this.customUrlSchemeHandler.handleUrl(this.userSeletedEmailDetailsData.body, this.userSeletedEmailDetailsData.attachments);
    //removing tailing spaces

    this.safeHtmlBody = this.sanitizer.bypassSecurityTrustHtml(this.safeHtmlBody.toString());

    this.modalService.open(this.emailDetailContent, {
      windowClass: 'modal-holder',
      size: 'xl',
    });
  }

  downloadAttachment(file) {
    const byteCharacters = atob(file.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: file.mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document?.body?.removeChild(a);
  }

  viewEmailListToDelete() {
    this.modalService.open(this.emailListContent, {
      windowClass: 'modal-holder',
      size: 'xl',
    });
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    const sortModel = [{ colId: 'dateTime', sort: 'desc' }];
  }

  gridOptionsData() {
    return {
      defaultColDef: {
        resizable: true,
        sortable: true,
        width: 150,
        cellClass: 'cell-class ag-right-aligned-cell',
        headerClass: 'header-class ag-left-aligned-header',
      },
      columnDefs: [
        {
          headerName: 'ACTION',
          width: 150,
          cellRenderer: 'actionRenderer',
          cellStyle: { 'text-align': 'left' },
        },
        {
          headerName: 'ARB Product Bulletin',
          field: 'subject',
          flex: 1,
          cellStyle: { 'text-align': 'left' },
        },
      ],
    };
  }
  viewProductBulletin(data) {
    this.showSelectedEmailDetails(data);
  }
  deleteProductBulletinConfirm(data) {
    //show confirmation dialog
    this.dialogService
      .confirm(`Are you sure you want to Delete this Product Bulletin?`)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((val) => {
        if (val) {
          this.deleteProductBulletin(data);
        }
      });
  }

  deleteProductBulletin(data) {
    //delete product bulletin
    this.apiService
      .deletePIBEmail({
        subject: data.subject,
        communicationGroup: data.communicationGroup,
      })
      .subscribe((res) => {
        //toast message
        if (res) this.toastr.success('Product bulletin deleted successfully');
        this.updatedUserEmailList.emit(true);
      });
  }

  addNewEmailBulletin() {
    this.isAddingNewEmailBulletin = true;
  }
  createNewEmailBulletin(data) {
    this.isAddingNewEmailBulletin = true;
  }
  updateEmailBulletin(data) {
    this.selectedEmail.emit(data);
  }
  closeAddingEmailBulletin(reload) {
    this.isAddingNewEmailBulletin = false;
    if (reload) {
      this.updatedUserEmailList.emit(true);
    }
  }
  showRows(){
    this.isShowRows = !this.isShowRows;
    this.pageViewChanged = true;
    this.getPaginationData();
  }

  ngOnDestroy(): void {}
}
